interface PdfPreviewProps {
  url: string;
  fileName?: string | null;
  variant?: 'full' | 'compact';
  helperText?: string;
}

function DocIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export function PdfPreview({
  url,
  fileName,
  variant = 'full',
  helperText,
}: PdfPreviewProps) {
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-canvas p-3">
        <div className="h-11 w-10 shrink-0 rounded-lg bg-accent-soft flex items-center justify-center">
          <DocIcon className="h-5 w-5 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink truncate">
            {fileName || 'PDF document'}
          </p>
          <p className="text-xs text-ink-muted">PDF</p>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1 min-h-tap min-w-tap justify-center text-sm font-semibold text-accent"
          aria-label="Open PDF in new tab"
        >
          <ExternalLinkIcon className="h-4 w-4" />
          Open
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center py-8 px-4 bg-canvas gap-4">
        <div className="h-16 w-14 rounded-lg bg-accent-soft flex items-center justify-center">
          <DocIcon className="h-8 w-8 text-accent" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider text-accent bg-accent-soft px-2 py-0.5 rounded-full">
          PDF
        </span>
        <p className="text-sm text-ink font-medium truncate max-w-[80%] text-center">
          {fileName || 'Uploaded document'}
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary gap-1.5"
        >
          <ExternalLinkIcon className="h-4 w-4" />
          Open PDF
        </a>
      </div>
      {helperText && (
        <p className="text-xs text-ink-muted px-3 py-2 border-t border-border bg-canvas">
          {helperText}
        </p>
      )}
    </>
  );
}
